export type PhotoPalette = readonly [string, string, string];

interface ColorBucket {
  red: number;
  green: number;
  blue: number;
  count: number;
}

function channelHex(value: number) {
  return Math.round(Math.min(255, Math.max(0, value))).toString(16).padStart(2, "0");
}

function visibleColor(red: number, green: number, blue: number) {
  const peak = Math.max(red, green, blue);
  const lift = peak < 72 ? 72 / Math.max(1, peak) : 1;
  const lifted = [red * lift, green * lift, blue * lift];
  const average = (lifted[0]! + lifted[1]! + lifted[2]!) / 3;
  const saturated = lifted.map((channel) => average + (channel - average) * 1.12);
  return `#${channelHex(saturated[0]!)}${channelHex(saturated[1]!)}${channelHex(saturated[2]!)}`;
}

function bucketColor(bucket: ColorBucket, fallback: readonly [number, number, number], adjustment: number) {
  if (bucket.count === 0) {
    return visibleColor(fallback[0] * adjustment, fallback[1] * adjustment, fallback[2] * adjustment);
  }
  return visibleColor(bucket.red / bucket.count, bucket.green / bucket.count, bucket.blue / bucket.count);
}

export function paletteFromRgba(pixels: Uint8ClampedArray): PhotoPalette | null {
  const buckets: ColorBucket[] = Array.from({ length: 3 }, () => ({ red: 0, green: 0, blue: 0, count: 0 }));
  const total: ColorBucket = { red: 0, green: 0, blue: 0, count: 0 };

  for (let offset = 0; offset + 3 < pixels.length; offset += 4) {
    const alpha = pixels[offset + 3] ?? 0;
    if (alpha < 128) continue;
    const red = pixels[offset] ?? 0;
    const green = pixels[offset + 1] ?? 0;
    const blue = pixels[offset + 2] ?? 0;
    const luminance = (red * 0.2126 + green * 0.7152 + blue * 0.0722) / 255;
    const bucketIndex = luminance < 0.34 ? 0 : luminance < 0.7 ? 1 : 2;
    const bucket = buckets[bucketIndex]!;
    bucket.red += red;
    bucket.green += green;
    bucket.blue += blue;
    bucket.count += 1;
    total.red += red;
    total.green += green;
    total.blue += blue;
    total.count += 1;
  }

  if (total.count === 0) return null;
  const fallback = [total.red / total.count, total.green / total.count, total.blue / total.count] as const;
  return [
    bucketColor(buckets[0]!, fallback, 0.72),
    bucketColor(buckets[1]!, fallback, 1),
    bucketColor(buckets[2]!, fallback, 1.24)
  ];
}

export async function samplePhotoPalette(file: File): Promise<PhotoPalette | null> {
  if (typeof createImageBitmap !== "function") return null;
  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = 36;
    canvas.height = 36;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return null;
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return paletteFromRgba(context.getImageData(0, 0, canvas.width, canvas.height).data);
  } catch {
    return null;
  } finally {
    bitmap?.close();
  }
}
